import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, ArrowLeft, Building2, Receipt, Package, Wallet, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const STEPS = [
  { key: "profile",   label: "Profile",   Icon: Building2 },
  { key: "gst",       label: "GST · Tax", Icon: Receipt   },
  { key: "catalog",   label: "Catalogue", Icon: Package   },
  { key: "payout",    label: "Payout",    Icon: Wallet    },
];

/**
 * Business Onboarding Wizard — 4 quick steps that turn a signed-in
 * personal account into a fully operational business seller:
 *   1) Profile      → POST /api/business
 *   2) GST + Tax    → PATCH /api/business/{id}
 *   3) First item   → POST /api/catalog/item
 *   4) Payout / VPA → PATCH /api/business/{id}   (stored as `payout_vpa`)
 *
 * Every step is skippable except Profile — the wizard tracks progress
 * client-side and posts the created business_id forward. On completion
 * the user lands on `/business/dashboard`.
 */
export default function BusinessOnboardingWizard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [biz, setBiz] = useState({ business_id: null });
  const [form, setForm] = useState({
    name: "", category: "", description: "", city: "", phone: user?.mobile || "", email: "",
    gstin: "", legal_name: "", pan: "", registered_address: "", state_code: "", country: "IN",
    item_name: "", item_price_rupees: "", item_desc: "",
    payout_vpa: "",
  });

  if (!user) return <div className="p-12 text-center text-stone-500">Sign in to start onboarding.</div>;

  const advance = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back    = () => setStep((s) => Math.max(0, s - 1));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitStep = async () => {
    setBusy(true);
    try {
      if (step === 0) {
        if (!form.name || !form.category) { toast.error("Business name and category are required"); setBusy(false); return; }
        const r = await api.post("/business", {
          name: form.name, category: form.category, description: form.description || null,
          city: form.city || null, phone: form.phone || null, email: form.email || null,
        });
        setBiz({ business_id: r.data.business_id });
        toast.success("Business profile created");
        advance();
      } else if (step === 1) {
        if (biz.business_id && (form.gstin || form.legal_name || form.pan)) {
          await api.patch(`/business/${biz.business_id}`, {
            gstin: form.gstin || null, legal_name: form.legal_name || null,
            pan: form.pan || null, registered_address: form.registered_address || null,
            state_code: form.state_code || null, country: form.country || "IN",
          });
          toast.success("GST details saved");
        } else {
          toast.info("Skipped — you can add GST later from Business settings");
        }
        advance();
      } else if (step === 2) {
        if (form.item_name && form.item_price_rupees) {
          const paise = Math.round(parseFloat(form.item_price_rupees) * 100);
          await api.post("/catalog/item", {
            business_id: biz.business_id,
            name: form.item_name,
            description: form.item_desc || null,
            unit_price_paise: paise,
            currency: "inr",
            status: "active",
          });
          toast.success("First product added to your catalogue");
        } else {
          toast.info("Skipped — add products later from Catalogue");
        }
        advance();
      } else if (step === 3) {
        if (form.payout_vpa && biz.business_id) {
          await api.patch(`/business/${biz.business_id}`, { payout_vpa: form.payout_vpa });
          toast.success("Payout destination saved");
        }
        toast.success("Setup complete — welcome aboard!");
        nav(`/business/dashboard?business_id=${biz.business_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Step failed — please try again");
    } finally {
      setBusy(false);
    }
  };

  const Step = STEPS[step].Icon;
  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="business-onboarding">
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs text-stone-500 font-bold uppercase tracking-widest">
          <span>Business setup · Step {step + 1} of {STEPS.length}</span>
          <span>{progress}%</span>
        </div>
        <div className="h-1.5 bg-stone-200 rounded-full mt-2 overflow-hidden">
          <div className="h-full bg-emerald-600 transition-all" style={{ width: `${progress}%` }} data-testid="onboarding-progress-bar" />
        </div>
      </div>

      <div className="flex justify-between mb-8 relative">
        {STEPS.map((s, i) => {
          const done = i < step, active = i === step;
          return (
            <div key={s.key} className="flex flex-col items-center flex-1" data-testid={`onboarding-step-marker-${s.key}`}>
              <div className={`w-10 h-10 rounded-full grid place-items-center border-2 ${
                done ? "bg-emerald-600 border-emerald-600 text-white" :
                active ? "border-emerald-600 text-emerald-600 bg-white" :
                "border-stone-300 text-stone-400 bg-white"}`}>
                {done ? <Check size={16} /> : <s.Icon size={16} />}
              </div>
              <div className={`mt-1 text-[11px] font-bold ${done || active ? "text-stone-900" : "text-stone-400"}`}>{s.label}</div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-6 space-y-4" data-testid={`onboarding-step-${STEPS[step].key}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 grid place-items-center text-emerald-700"><Step size={20} /></div>
          <div>
            <h2 className="text-xl font-black text-stone-900">
              {step === 0 && "Tell us about your business"}
              {step === 1 && "GST · legal identity"}
              {step === 2 && "Add your first product"}
              {step === 3 && "Where should we pay you?"}
            </h2>
            <p className="text-sm text-stone-500">
              {step === 0 && "Basic profile — this is what customers see."}
              {step === 1 && "Optional now, but required to send GST invoices."}
              {step === 2 && "Skip for now if your catalogue lives elsewhere."}
              {step === 3 && "UPI VPA is fastest. You can change this later."}
            </p>
          </div>
        </div>

        {step === 0 && (
          <div className="space-y-3">
            <Input placeholder="Business name *" value={form.name} onChange={e => set("name", e.target.value)} data-testid="ob-name"/>
            <Input placeholder="Category (e.g. Restaurant, Salon, Retail) *" value={form.category} onChange={e => set("category", e.target.value)} data-testid="ob-category"/>
            <Textarea placeholder="What do you do? (2-3 lines)" value={form.description} onChange={e => set("description", e.target.value)} rows={3} data-testid="ob-description"/>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="City" value={form.city} onChange={e => set("city", e.target.value)} data-testid="ob-city"/>
              <Input placeholder="Phone" value={form.phone} onChange={e => set("phone", e.target.value)} data-testid="ob-phone"/>
            </div>
            <Input placeholder="Email (optional)" value={form.email} onChange={e => set("email", e.target.value)} data-testid="ob-email"/>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <Input placeholder="GSTIN (15 chars) — optional" value={form.gstin} onChange={e => set("gstin", e.target.value.toUpperCase())} maxLength={15} data-testid="ob-gstin"/>
            <Input placeholder="Registered legal name" value={form.legal_name} onChange={e => set("legal_name", e.target.value)} data-testid="ob-legal-name"/>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="PAN" value={form.pan} onChange={e => set("pan", e.target.value.toUpperCase())} maxLength={10} data-testid="ob-pan"/>
              <Input placeholder="State code (e.g. 27 for MH)" value={form.state_code} onChange={e => set("state_code", e.target.value)} maxLength={2} data-testid="ob-state"/>
            </div>
            <Textarea placeholder="Registered address" value={form.registered_address} onChange={e => set("registered_address", e.target.value)} rows={2} data-testid="ob-address"/>
            <p className="text-xs text-stone-500">Skip this step if you're not GST-registered — you can add these details later.</p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <Input placeholder="Product name" value={form.item_name} onChange={e => set("item_name", e.target.value)} data-testid="ob-item-name"/>
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" placeholder="Price (₹)" value={form.item_price_rupees} onChange={e => set("item_price_rupees", e.target.value)} data-testid="ob-item-price"/>
              <div className="text-xs text-stone-500 self-center">You can add more products anytime from Catalogue.</div>
            </div>
            <Textarea placeholder="Description (optional)" value={form.item_desc} onChange={e => set("item_desc", e.target.value)} rows={2} data-testid="ob-item-desc"/>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <Input placeholder="Your UPI ID (e.g. yourname@bank)" value={form.payout_vpa} onChange={e => set("payout_vpa", e.target.value)} data-testid="ob-payout-vpa"/>
            <p className="text-xs text-stone-500">Payouts run every 24 hours after successful order deliveries. You can add a bank account later.</p>
          </div>
        )}

        <div className="pt-2 flex items-center justify-between">
          <Button variant="ghost" onClick={back} disabled={busy || step === 0} className="rounded-full text-stone-500" data-testid="ob-back">
            <ArrowLeft size={14} className="mr-1"/> Back
          </Button>
          <Button onClick={submitStep} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700 font-black" data-testid="ob-next">
            {busy ? <Loader2 size={14} className="animate-spin"/> : (step === STEPS.length - 1 ? "Finish" : (<>Continue <ArrowRight size={14} className="ml-1"/></>))}
          </Button>
        </div>
      </div>
    </div>
  );
}
